import {
  BaseChart,
  IChart,
  IChartData,
  IAverageLine,
  IAxis,
  IChartSeries,
  IGroupBarTooltipData,
  IStackedChartTooltip
} from '../models';
import { CHART_TYPE, CHART_DATA_VALUE_TYPE, DATA_TYPE, STACKED_BAR_CONSTANTS, STACKED_BAR_CHART_TOOLTIP_BEHAVIOR } from '../constants';
import { getFormattedNumberAbbrev } from '../utils';
import { getAxisScaleBand, getAxisScaleLinear } from '../utils/axis-helper';
import { axisBottom, axisLeft } from 'd3-axis';

/********* Interfaces **********************/
export interface IBarChart extends IChart {
  width?: number;
  height?: number;
  isHorizontal: boolean;
  isSmallChart: boolean;
  barWidth?: number;
  barHeight?: number;
  barPadding?: number;
  average?: IAverageLine[];
  xAxis?: IAxis;
  yAxis?: IAxis;
  maxValue?: number;
  hideDataLabels?: boolean;
  dataLabelOffset?: number;
  groupBarSpacing?: number;
  stretchToMaxWidth?: boolean;
  interGroupSpacing?: number;
  enableCredibilityTooltip?: boolean;
}

export interface IBoxPlot extends IBarChart { }

export interface IStackedBar extends IBarChart {
  margin?: any;
}

/********* Defaults **********************/
const DEFAULT_WIDTH = 800; // default svg width
const DEFAULT_HEIGHT = 400; // default svg height
const DEFAULT_BARWIDTH = 60; // default width of bar in vertical charts
const DEFAULT_BARHEIGHT = 16; // default height of bar in horizontal charts
const DEFAULT_BOXPLOT_BARHEIGHT = 12; // default height of bar in horizontal boxplot charts
const DEFAULT_BOXPLOT_BARPADDING = 60; // default added space for spaces between bars used in calculated height for simple bar chart
const DEFAULT_BARPADDING = 12; // default added space for spaces between bars used in calculated height for simple bar chart
const DEFAULT_LABEL_OFFSET = 5; // caliberated for horizontal chart; distance between bar and data label
const DEFAULT_GROUPBAR_SPACNG = 2; // default space for spaces between bars used in calculated height for grouped bar chart
const DEFAULT_INTERGROUP_SPACNG = 20; // default added distance for spaces between groups calculated height for grouped bar chart
const TOPMOST_GROUPBAR_OFFSET = 18;

/********* Bar Chart Model **********************/
export class BarChart extends BaseChart implements IBarChart {
  public height!: number;
  public width!: number;
  public isHorizontal: boolean;
  public isSmallChart: boolean;
  public barWidth: number;
  public barHeight: number;
  public average: IAverageLine[];
  public xAxis: IAxis;
  public yAxis: IAxis;
  public barPadding: number;
  public dataLabelOffset: number;
  public groupBarSpacing: number;
  public stretchToMaxWidth: boolean;
  public enableCredibilityTooltip: boolean;
  public groupedBarTooltipData: IGroupBarTooltipData[];
  public interGroupSpacing: number;
  public uniqueGroups: any[];
  public groupAxisBottomOffset?: number;
  public plotAreaWidth: number;
  public maxValue?: number;
  private _noOfAxisLines: number;


  constructor(obj: IBarChart) {
    super(obj);
    Object.assign(this, obj);
    this.enableCredibilityTooltip = obj.enableCredibilityTooltip || false;
    this.barPadding = obj.barPadding || DEFAULT_BARPADDING;
    this.barWidth = obj.barWidth || DEFAULT_BARWIDTH;
    this.barHeight = obj.barHeight || DEFAULT_BARHEIGHT;
    this.width = obj.width || DEFAULT_WIDTH;
    this.isHorizontal = obj.isHorizontal || false;
    this.isSmallChart = obj.isSmallChart || false;
    this.maxValue = obj.maxValue || null;
    this.stretchToMaxWidth = obj.stretchToMaxWidth || false;
    this.average = obj.average || [];
    this.xAxis = obj.xAxis || {};
    this.yAxis = obj.yAxis || {};
    this.dataLabelOffset = obj.dataLabelOffset || DEFAULT_LABEL_OFFSET;
    this.groupBarSpacing = obj.groupBarSpacing || DEFAULT_GROUPBAR_SPACNG;
    this.interGroupSpacing = obj.interGroupSpacing || DEFAULT_INTERGROUP_SPACNG;
    this.groupedBarTooltipData = this.getGroupedBarTooltipData();
    this.height = obj.height || this.getCalculatedHeight();
    this.plotAreaWidth = this.width * 0.95;
  }

  public get data(): IChartSeries[] {
    return this.series;
  }

  public get valuePrefix(): string {
    return this.series[0]?.valuePrefix;
  }

  public get valueSuffix(): string {
    return this.series[0]?.valueSuffix;
  }

  public get xDomain(): string[] {
    return this.series[0]?.data?.map(d => d.name);
  }

  public get minDigitsToAbbreviate(): number {
    // Take first series as representative;
    return this.series[0]?.minDigitsToAbbreviate;
  }

  public get decimalPlaces(): number {
    // Take first series as representative;
    return this.series[0]?.decimalPlaces;
  }

  public get xAxisBottomOffset(): number {
    const xAxisWrappedLinesOffset =
      this.noOfAxisLines && this.noOfAxisLines > 1
        ? (this.noOfAxisLines - 1) * 20 //2o extra offset for each wrapped line
        : 0;

    return 70 + xAxisWrappedLinesOffset;
  }

  public get plotAreaHeight(): number {
    // 70 -> for yaxis title
    return this.height - this.xAxisBottomOffset; // TODO: should be specific numeric instead of percentage. as x-axis title been position numerically
  }

  public get xAxisTickSize(): number {
    return this.xAxis.showGridLines && this.isHorizontal
      ? -this.plotAreaHeight
      : 0;
  }

  public get yAxisTickSize(): number {
    return this.yAxis.showGridLines && !this.isHorizontal
      ? -this.plotAreaWidth
      : 0;
  }

  public get widthModifier(): number {
    return this.isHorizontal && !this.isSmallChart ? 0.85 : 0.95;
  }

  public get noOfAxisLines(): number {
    return this._noOfAxisLines;
  }

  public set noOfAxisLines(value: number) {
    this._noOfAxisLines = value;
  }

  public get plotAreaLeftMargin(): number {
    return this.isSmallChart ? 30 : this.isHorizontal ? 120 : 60;
  }

  public get yAxisTitleLeftMargin(): number {
    return -(this.plotAreaLeftMargin - 20);
  }

  public get yAxisSvgLeftMargin(): number {
    return this.isHorizontal
      ? this.plotAreaLeftMargin - 50
      : this.plotAreaLeftMargin;
  }

  public get yAxisTickPadding(): number {
    return 40;
  }

  public get plotAreaTopMargin(): number {
    if(this.isSmallChart && !this.average.length) {
      return 20;
    }
    
    return this.isHorizontal && !this.average.length ? 0 : 20; // TODO: need top margin for horizontal chart??
  }

  public get scaleBandOuterPadding(): number {
    return this.isHorizontal ? 0 : 0.5;
  }

  public get yDomain(): number[] {
    const yValues = Array.prototype.concat
      .apply(
        [],
        this.series.map(s => s.data)
      )
      .filter(d => !d.isHidden)
      .map(d => d.y);
    const maxVal = Math.max(...yValues, ...this.average.map(a => a.y));

    // Y Axis domain to be set from 0 to maximum value with an additional 20% allowance
    return [0, maxVal + maxVal * 0.2];
  }

  public get yScale(): any {
    return this.isHorizontal
      ? getAxisScaleBand(
        this.xDomain,
        0,
        this.plotAreaHeight,
        0,
        this.scaleBandOuterPadding
      )
      : getAxisScaleLinear(this.yDomain, this.plotAreaHeight, 0);
  }

  public get xScale(): any {
    return this.isHorizontal
      ? getAxisScaleLinear(
        this.yDomain,
        0,
        this.plotAreaWidth - 10,
        this.xAxis.tickValues
      )
      : getAxisScaleBand(
        this.xDomain,
        0,
        this.plotAreaWidth - 10,
        0,
        this.scaleBandOuterPadding
      );
  }

  public get leftAxis(): any {
    const ticks = !this.isHorizontal ? this.yScale.ticks(5) : [];
    const decimalPlace = this.getFormattedAxisTickDecimalPlace(ticks, false);

    const leftAxis = axisLeft(this.yScale)
      .tickFormat(x => this.getFormattedAxisTick(x.toString(), false, decimalPlace)
        ?.replace(/\.0/, '')
      )
      .ticks(5)
      .tickSize(this.yAxisTickSize);

    if (this.isHorizontal) {
      leftAxis.tickPadding(this.yAxisTickPadding);
    }

    if (this.isSmallChart) {
      leftAxis.tickValues([]);
    }

    return leftAxis;
  }

  public get bottomAxis(): any {
    const ticks = this.isHorizontal ? this.xScale.ticks(5) : [];
    const decimalPlace = this.getFormattedAxisTickDecimalPlace(ticks, true);

    const bottomAxis = axisBottom(this.xScale)
      .tickSize(this.xAxisTickSize)
      .tickFormat(x => this.getFormattedAxisTick(x.toString(), true, decimalPlace)
        ?.replace(/\.0/, '')
      )
      .ticks(5)
      .tickPadding(10);

    if (this.isHorizontal) {
      bottomAxis.tickSizeOuter(5);
    }

    return bottomAxis;
  }

  //TODO: Revisit for grouped chart
  public getCalculatedHeight(): number {
    if (!this.isShowChart || !this.isHorizontal) {
      return DEFAULT_HEIGHT;
    }

    let height = this.xAxisBottomOffset; // initial offset for the x-axis bottom area
    let noOfBars = this.series[0]?.data.filter(d => !d.isHidden).length;

    if (this.type == CHART_TYPE.GROUPED_BAR) {
      if (!this.uniqueGroups) {
        this.getUniqueGroups();
      }

      const totalNoOfBars = this.series.flatMap((series) => series.data).filter((data) => !data.isHidden).length;

      height +=
        totalNoOfBars * this.barHeight +
        (totalNoOfBars - 1) * this.groupBarSpacing;
      height += (this.uniqueGroups.length - 1) * this.interGroupSpacing;
      height +=
        this.uniqueGroups.length <= 2 ? this.interGroupSpacing * 0.5 : 0;

      return this.isSmallChart
        ? height + this.uniqueGroups.length * 20 // extra space (20 per group) for data labels on top of group for small chart
        : height;
    }

    height +=
      noOfBars * this.barHeight + // total height based on data points
      (noOfBars - 1) * this.barPadding +
      20; //add 20 offset for distance between last bar and x axis

    return this.isSmallChart ? height + noOfBars * 20 : height;
  }

  public displayNoBenchmarkIcon(groupName): boolean {
    return this.series.find(series =>
      series?.data?.find(
        d => d.name == groupName && d.type == DATA_TYPE.BENCHMARK_INDICATOR
      )
    )
      ? true
      : false;
  }

  // TODO: Find exact d3 type instead of "any"
  public getBarHeight(yScale: any, data: IChartData): number {
    return this.isHorizontal
      ? this.barHeight
      : this.plotAreaHeight - yScale(data.y);
  }

  public getUniqueGroups() {
    //Get unique data labels(category axis) across the series
    return (this.uniqueGroups = [
      ...new Set(
        this.series.reduce((prev, next) => {
          return prev.concat(next.data.map(m => m.name));
        }, [])
      )
    ]);
  }

  public getGroupedBarTooltipData(): IGroupBarTooltipData[] {
    if (!this.series) {
      return;
    }

    if (!this.tooltip) {
      return null;
    }

    let groupTooltipData = [];
    const benchmarkSeries = this.series.find(s => s.isBenchmarkSeries);

    if (!this.uniqueGroups) {
      this.getUniqueGroups();
    }

    this.uniqueGroups.forEach(groupName => {
      const tooltipData = this.series.map(series => {
        const barData = series.data.find(d => d.name === groupName);
        if (!barData) {
          return;
        }

        const barValue = barData ? barData.y : 0;
        const bechmarkValue = benchmarkSeries
          ? (benchmarkSeries.data.find(d => d.name == barData.name) || {}).y
          : 0;
        const differenceWithBenchmark =
          series.isBenchmarkSeries ||
            this.type !== CHART_TYPE.GROUPED_BAR ||
            !bechmarkValue
            ? ''
            : `(${Math.round((barValue / bechmarkValue - 1) * 100)}%)`; // TODO: configuration for rounding; round to 2 decimal places

        const formattedBarValue =
          this.series[0]?.valueType == CHART_DATA_VALUE_TYPE.CURRENCY
            ? getFormattedNumberAbbrev(barValue, this.formatCountryCode, 0, this.minDigitsToAbbreviate).toString()
            : barValue.toString();

        return {
          barName:
            this.type === CHART_TYPE.GROUPED_BAR ? series.name : barData.name,
          barValue: this.getValueWithPrefixAndSuffix(formattedBarValue),
          dataValue: barData?.y || 0,
          displayLabelY: this.getValueWithPrefixAndSuffix(barData.displayLabelY),
          differenceWithBenchmark: differenceWithBenchmark,
          isHidden: barData.isHidden,
          color: barData.color
        };
      });

      groupTooltipData.push({
        name: groupName,
        tooltipData: tooltipData
      });
    });

    return groupTooltipData;
  }

  public getTooltipBarName(seriesName: string, barDataName: string): string {
    switch (this.type) {
      case CHART_TYPE.GROUPED_BAR:
      case CHART_TYPE.STACKED_BAR:
        return seriesName;
      default:
        return barDataName;
    }
  }

  // TODO: Find exact d3 type instead of "any"
  public getBarWidth(xScale: any, data: IChartData): number {
    return this.isHorizontal ? xScale(data.y) : this.barWidth;
  }

  // TODO: Find exact d3 type instead of "any"
  public getBarOriginCoordinateX(
    xScale: any,
    data: IChartData,
    seriesIndex: number
  ): number {
    switch (this.type) {
      case CHART_TYPE.STACKED_BAR:
        return this.isHorizontal
          ? 0
          : xScale(data.name);

      default:
        return this.isHorizontal
          ? 0
          : xScale(data.name) +
          seriesIndex * (this.barWidth + this.groupBarSpacing);
    }
  }

  // TODO: Find exact d3 type instead of "any"
  public getBarOriginCoordinateY(
    yScale: any,
    data: IChartData,
    seriesIndex: number
  ): number {
    switch (this.type) {
      //Group bar chart
      case CHART_TYPE.GROUPED_BAR:
        const horizontalYCoord =
          yScale(data.name) +
          seriesIndex * (this.barHeight + this.groupBarSpacing);

        const isTopMostBarHidden = this.series[0].data.find(d => d.name == this.uniqueGroups[0]).isHidden;
        return this.isHorizontal
          ? this.isSmallChart
            ? horizontalYCoord + (isTopMostBarHidden
              ? 10
              : (this.isBenchmarkHidden(data)
                ? TOPMOST_GROUPBAR_OFFSET + (this.barHeight * 0.5)
                : TOPMOST_GROUPBAR_OFFSET)) // offset for topmost data label in small horizontal chart
            : horizontalYCoord
          : yScale(data.y);
      default:
        return this.isHorizontal
          ? this.isSmallChart
            ? yScale(data.name) + 18
            : yScale(data.name)
          : yScale(data.y);
    }
  }

  public isBenchmarkHidden(data: IChartData): boolean {
    return (this.series.length > 1 && !!this.series.find(s => s.isBenchmarkSeries)?.data.find(d => d.name == data.name && d.isHidden))
  }

  // TODO: Find exact d3 type instead of "any"
  public getDataLabelCoordinateX(
    xScale: any,
    data: IChartData,
    seriesIndex: number
  ): number {
    const xCoord = this.isHorizontal
      ? this.getBarWidth(xScale, data) + this.dataLabelOffset
      : this.getBarOriginCoordinateX(xScale, data, seriesIndex) +
      this.getBarWidth(xScale, data) / 2;

    return xCoord;
  }

  // TODO: Find exact d3 type instead of "any"
  public getDataLabelCoordinateY(
    yScale: any,
    data: IChartData,
    seriesIndex: number
  ): number {
    if (this.yDomain)
      return this.isHorizontal
        ? this.getBarOriginCoordinateY(yScale, data, seriesIndex) + 20
        : yScale(data.y) - 10;
  }

  public getFormattedAxisTick(
    tickValue: string,
    isXAxis: boolean,
    decimalPlace: number = 0
  ) {
    if ((!isXAxis && this.isHorizontal) || (isXAxis && !this.isHorizontal)) {
      return tickValue;
    }

    return `${this.valuePrefix || ''}${getFormattedNumberAbbrev(
      +tickValue,
      this.formatCountryCode,
      decimalPlace,
      this.minDigitsToAbbreviate
    ).toString()} ${this.valueSuffix || ''}`;
  }

  public getFormattedAxisTickDecimalPlace(
    ticks: any,
    isXAxis: boolean,
  ) {
    // if any of the formatted tick values are identical then round using one decimal place
    const formattedValues = ticks.map(x => this.getFormattedAxisTick(x?.toString(), isXAxis, 0));
    return new Set(formattedValues)?.size != ticks?.size
      ? 1
      : 0;
  }

  public getFormattedDataLabel(tickValue: string, secondaryDataLabel: string, decimalPlaces = 0) {
    return secondaryDataLabel
      ? secondaryDataLabel
      : this.series[0]?.valueType == CHART_DATA_VALUE_TYPE.CURRENCY
        ? getFormattedNumberAbbrev(+tickValue, this.formatCountryCode, decimalPlaces, this.minDigitsToAbbreviate).toString()
        : tickValue;
  }

  public getValueWithPrefixAndSuffix(value: string): string {
    return value
      ? `${this.valuePrefix || ''}${value}${this.valueSuffix || ''}`
      : '';
  }
}

export class SingleBarChart extends BarChart {
  constructor(obj: IBarChart) {
    super(obj);
  }
}

export class GroupedBarChart extends BarChart {
  constructor(obj: IBarChart) {
    super(obj);
  }
}

export class BoxPlotChart extends BarChart {
  public boxplotBarPadding: number;

  constructor(obj: IBoxPlot) {
    super(obj);
    this.barHeight = obj.barHeight || DEFAULT_BOXPLOT_BARHEIGHT;
    this.boxplotBarPadding = obj.barPadding || DEFAULT_BOXPLOT_BARPADDING;
  }

  public get xDomain(): string[] {
    return this.series.map(s => s.name);
  }

  public get maxYVal(): number {
    let maxVal = 0;

    this.series.forEach(series => {
      series.data.forEach(data => {
        maxVal = maxVal > data.y ? maxVal : data.y;
      });
    });

    return maxVal;
  }

  public get customPointName(): string {
    const data = this.getBoxPlotDataPoint(this.series[0], DATA_TYPE.CUSTOM_POINT)
    if (data == null) return null
    return typeof data.name != undefined ? data.name : null
  }

  public get plotAreaHeight(): number {
    return this.height - 60;
  }

  //TODO: Revisit for grouped chart
  public getCalculatedHeight(): number {
    if (!this.isShowChart || !this.isHorizontal) {
      return DEFAULT_HEIGHT;
    }

    this.boxplotBarPadding =
      this.boxplotBarPadding || DEFAULT_BOXPLOT_BARPADDING;

    return (
      this.series.length * (this.barHeight + this.boxplotBarPadding) + 30 // extra 30 offset for x axis label and barPadding is spacing between bars
    );
  }

  public getBoxPlotDataPoint(series: IChartSeries, dataType: DATA_TYPE) {
    const dataPoint = series.data.find(d => d.type == dataType);
    return dataPoint ? dataPoint : undefined;
  }

  public getBoxPlotYCoord(seriesName) {
    return this.yScale(seriesName) + 10; //offset to move boxplot closer to axis
  }

  public getLineYCoord(
    upperEndPoint: boolean,
    series: IChartSeries,
    secondline: IChartData
  ) {
    const offsetFromCentreLine = this.barHeight * (2 / 3);

    return secondline
      ? this.getBoxPlotYCoord(series.name)
      : upperEndPoint
        ? this.getBoxPlotYCoord(series.name) - offsetFromCentreLine
        : this.getBoxPlotYCoord(series.name) + offsetFromCentreLine;
  }
}

export class StackedBarChart extends BarChart {
  public tooltip?: IStackedChartTooltip;
  public margin?: number;
  public stackedBarTooltipData: IGroupBarTooltipData[];

  constructor(obj: IStackedBar) {
    super(obj);
    this.margin = obj.margin;
    this.stackedBarTooltipData = this.getStackedBarTooltipData();
  }

  public get maxYVal(): number {
    const kvp = this.series
      .map(s => s.data)
      .flat()
      .reduce((m, { name, y }) =>
        m.set(name, (m.get(name) || 0) + y),
        new Map);

    return Math.max(...kvp.values());
  }

  public get bottomAxis(): any {
    const ticks = this.isHorizontal ? this.xScale.ticks(5) : [];

    const bottomAxis = axisBottom(this.xScale)
      .tickSize(this.xAxisTickSize)
      .tickFormat((x, index) => {
        // if current & next axis tick generated identical. regenerate by adding extra decimal to diffentiate
        // e.g -> ticks - [0, 1, 1, 2, 2] transforms to [0, 0.5, 1, 1.5, 2]
        const decimalPlace =
          this.getFormattedAxisTick(x.toString(), true, 0) ===
            this.getFormattedAxisTick(ticks[index + 1], true, 0)
            ? 1
            : 0;
        return this.getFormattedAxisTick(x.toString(), true, decimalPlace);
      })
      .ticks(5)
      .tickPadding(10);

    if (this.isHorizontal) {
      bottomAxis.tickSizeOuter(5);
    }

    return bottomAxis;
  }

  public get yStackedDomain(): number[] {
    // Y Axis domain to be set from 0 to maximum value with an additional 20% allowance
    return [0, (this.maxValue ? this.maxValue : (this.maxYVal * 1.2))];
  }

  public get yScale(): any {
    return this.isHorizontal
      ? getAxisScaleBand(
        this.xDomain,
        0,
        this.plotAreaHeight,
        0,
        this.scaleBandOuterPadding
      )
      : getAxisScaleLinear(this.yStackedDomain, this.plotAreaHeight, 0);
  }

  public get xScale(): any {
    return this.isHorizontal
      ? getAxisScaleLinear(
        this.yStackedDomain,
        0,
        this.plotAreaWidth - 10,
        this.xAxis.tickValues
      )
      : getAxisScaleBand(
        this.xDomain,
        0,
        this.plotAreaWidth - 10,
        0,
        this.scaleBandOuterPadding
      );
  }

  public get isHighDensity(): boolean {
    return this.series.some(r => r.data.length >= STACKED_BAR_CONSTANTS.HIGH_DENSITY_THRESHOLD);
  }

  public get tooltipBehavior(): STACKED_BAR_CHART_TOOLTIP_BEHAVIOR {
    return this.tooltip.tooltipBehavior ?? STACKED_BAR_CHART_TOOLTIP_BEHAVIOR.ISOLATED;
  }

  public isTopBar(index: number): boolean {
    // On the assumption all series has same size
    return index === (this.series.length - 1);
  }

  public getBarOriginCoordinateX(
    xScale: any,
    data: IChartData,
    peak?: number,
  ): number {
    return this.isHorizontal
      ? xScale(peak)
      : xScale(data.name);
  }

  public getBarOriginCoordinateY(
    yScale: any,
    data: IChartData,
    acc: number,
  ): number {
    return this.isHorizontal
      ? this.isSmallChart
        ? yScale(data.name) + 15
        : yScale(data.name)
      : yScale(data.y + acc);
  }

  public getDataLabelCoordinateY(
    yScale: any,
    data: IChartData,
    peak: number,
  ): number {
    return this.isHorizontal
      ? this.getBarOriginCoordinateY(yScale, data, peak) + this.barHeight * 0.7
      : (yScale(peak) - 10) - this.barHeight * 0.4;
 
  }

  public getDataLabelCoordinateX(
    xScale: any,
    data: IChartData,
    peak?: number,
  ): number {
    return this.isHorizontal
      ? (xScale(peak) - 10) + this.barWidth * 0.4
      : this.getBarOriginCoordinateX(xScale, data) +
      this.getBarWidth(xScale, data) / 2;
  }

  public getStackedBarTooltipData(): IGroupBarTooltipData[] {
    if (!this.series) {
      return;
    }

    if (!this.tooltip) {
      return null;
    }

    let groupTooltipData = [];
    const benchmarkSeries = this.series.find(s => s.isBenchmarkSeries);

    if (!this.uniqueGroups) {
      this.getUniqueGroups();
    }

    this.uniqueGroups.forEach(groupName => {
      const tooltipData = this.series.map(series => {
        const barData = series.data.find(d => d.name === groupName);

        if (!barData) {
          return {
            barName: series.name,
            barValue: 0,
            dataValue: 0,
            displayLabelY: undefined,
            differenceWithBenchmark: '',
            isHidden: false,
            color: '',
          };
        }

        const barValue = barData ? barData.y : 0;
        const bechmarkValue = benchmarkSeries
          ? (benchmarkSeries.data.find(d => d.name == barData.name) || {}).y
          : 0;
        const differenceWithBenchmark =
          series.isBenchmarkSeries ||
            this.type !== CHART_TYPE.GROUPED_BAR ||
            !bechmarkValue
            ? ''
            : `(${Math.round((barValue / bechmarkValue - 1) * 100)}%)`; // TODO: configuration for rounding; round to 2 decimal places

        const formattedBarValue =
          this.series[0]?.valueType == CHART_DATA_VALUE_TYPE.CURRENCY
            ? getFormattedNumberAbbrev(barValue, this.formatCountryCode, this.decimalPlaces, this.minDigitsToAbbreviate).toString()
            : barValue.toString();

        return {
          barName: this.getTooltipBarName(series.name, barData.name),
          barValue: `${this.valuePrefix || ''}${formattedBarValue}${this.valueSuffix || ''}`,
          dataValue: barData?.y || 0,
          displayLabelY: barData.displayLabelY,
          differenceWithBenchmark: differenceWithBenchmark,
          isHidden: barData.isHidden,
          percentage: (barData?.y || 0) / this.maxYVal * 100
        };
      });

      groupTooltipData.push({
        name: groupName,
        tooltipData: tooltipData
      });
    });

    return groupTooltipData;
  }
}
